import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface LicenseValidationResult {
  valid: boolean;
  reason: string;
  details?: {
    plan_name?: string;
    plan_type?: string;
    system_name?: string;
    system_code?: string;
    is_trial?: boolean;
    trial_started_at?: string;
    trial_expires_at?: string;
    expired?: boolean;
  };
  company_id?: string;
  system_id?: string;
  system_code?: string;
}

export function useLicenseValidation(companyId?: string, systemCode?: string) {
  return useQuery({
    queryKey: ['license-validation', companyId, systemCode],
    queryFn: async (): Promise<LicenseValidationResult> => {
      if (!companyId) {
        return {
          valid: false,
          reason: 'Company ID não fornecido'
        };
      }

      const { data, error } = await supabase.functions.invoke('validate-license-access', {
        body: {
          company_id: companyId,
          system_code: systemCode
        }
      });

      if (error) {
        console.error('Error validating license:', error);
        return {
          valid: false,
          reason: 'Erro ao validar licença'
        };
      }

      return data;
    },
    enabled: !!companyId,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
  });
}

export function useBatchLicenseValidation(companyId?: string, systemCodes?: string[]) {
  return useQuery({
    queryKey: ['batch-license-validation', companyId, systemCodes],
    queryFn: async (): Promise<Record<string, LicenseValidationResult>> => {
      if (!companyId || !systemCodes || systemCodes.length === 0) {
        return {};
      }

      const results: Record<string, LicenseValidationResult> = {};

      // Validate each system license
      for (const systemCode of systemCodes) {
        try {
          const { data, error } = await supabase.functions.invoke('validate-license-access', {
            body: {
              company_id: companyId,
              system_code: systemCode
            }
          });

          if (error) {
            results[systemCode] = {
              valid: false,
              reason: 'Erro ao validar licença'
            };
          } else {
            results[systemCode] = data;
          }
        } catch (err) {
          results[systemCode] = {
            valid: false,
            reason: 'Erro de conexão'
          };
        }
      }

      return results;
    },
    enabled: !!companyId && !!systemCodes && systemCodes.length > 0,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
  });
}